<template>
  <div v-if="orderSummary" class="order-summary">
    <div class="card">
      <div class="card-header header-title">ご注文内容の確認</div>

      <div class="card-body">
        <table class="table">
          <tbody>
            <tr>
              <th class="custom-title">お名前</th>
              <td>{{ billingAddressData.name01 }} {{ billingAddressData.name02 }}</td>
            </tr>
            <tr>
              <th class="custom-title">住所</th>
              <td>
                〒{{ billingAddressData.zip01 }}{{ billingAddressData.zip02 }}
                {{ findByCode(billingAddressData.prefectureId)?.name }}
                {{ billingAddressData.addr01 }}
                {{ billingAddressData.addr02 }}
              </td>
            </tr>
          </tbody>
        </table>

        <table class="table">
          <thead>
            <tr class="custom-title">
              <th>商品名</th>
              <th>単価</th>
              <th>個数</th>
              <th class="text-end">小計</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="variant in orderSummary.variants" :key="variant.id">
              <td style="white-space: pre-line" class="w-50 text-break">{{ variant.name }}</td>
              <td>{{ filters.currency(variant.salesPrice) }}</td>
              <td>{{ variant.quantity }} 個</td>
              <td class="text-end">{{ filters.currency(variant.total) }}</td>
            </tr>

            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>

            <tr>
              <td></td>
              <td></td>

              <td class="custom-title text-end">小計</td>
              <td class="text-end">
                {{ filters.currency(orderSummary.subtotal) }}
              </td>
            </tr>

            <tr v-if="orderSummary.discount || orderSummary.couponCode">
              <td></td>
              <td></td>

              <td class="custom-title text-end">割引</td>
              <td class="text-end">-{{ filters.currency(orderSummary.discount || 0) }}</td>
            </tr>

            <tr>
              <td></td>
              <td></td>

              <td class="custom-title text-end">送料</td>
              <td class="text-end">{{ filters.currency(orderSummary.delivFee) }}</td>
            </tr>

            <tr>
              <td></td>
              <td></td>

              <td class="custom-title text-end">手数料</td>
              <td class="text-end">{{ filters.currency(orderSummary.charge) }}</td>
            </tr>

            <tr>
              <td></td>
              <td></td>

              <td class="custom-title text-end">消費税</td>
              <td class="text-end">{{ filters.currency(orderSummary.tax) }}</td>
            </tr>

            <tr>
              <td></td>
              <td></td>

              <td class="custom-title text-end">合計</td>
              <td class="text-end text-danger fw-700">
                {{ filters.currency(orderSummary.total) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { useField } from "vee-validate";
import { omit, debounce } from "lodash";
import { findByCode } from "jp-prefectures";

import { storeToRefs } from "pinia";
import { useCartStore } from "@/stores/cart";
import { useValidationStore } from "@/stores/form/validation";

import filters from "@/composables/filters";

import {
  ProductInput,
  PaymentInput,
  UrlInput,
  AddressInput,
  ShippingAddressInput,
} from "@/types/input/CartInput";
import { PublicsOrdersPricesType } from "@/types/publics/orders/PricesType";

const cartStore = useCartStore();

const validationStore = useValidationStore();
const { numberOfRemainFields } = storeToRefs(validationStore);

const { value: urlData } = useField<UrlInput>("urlData");
const { value: productsData } = useField<Array<ProductInput>>("productsData");
const { value: billingAddressData } = useField<AddressInput>("billingAddressData");
const { value: shippingAddressData } = useField<ShippingAddressInput>("shippingAddressData");
const { value: paymentData } = useField<PaymentInput>("paymentData");
const { value: shippingCarrierId } = useField<String>("shippingCarrierId");
const { value: couponCode } = useField<string>("couponCode");

const orderSummary = ref<PublicsOrdersPricesType | null>(null);
const calculateLoading = ref(false);

const calculatePriceOrder = async () => {
  if (numberOfRemainFields.value != 0) return (calculateLoading.value = false);

  const productsDataInput = productsData.value.map((product: ProductInput) => {
    return omit(product, ["isRecurring"]);
  });

  const input = {
    baseUrl: urlData.value.baseUrl,
    productsData: productsDataInput,
    billingAddressData: { prefectureId: billingAddressData.value.prefectureId },
    shippingAddressData: {
      prefectureId: shippingAddressData.value.prefectureId,
      sameWithBillingAddress: shippingAddressData.value.sameWithBillingAddress,
    },
    paymentData: { paymentMethodCode: paymentData.value.paymentMethodCode },
    shippingCarrierId: shippingCarrierId.value,
    couponCode: couponCode.value || null,
  };

  orderSummary.value = await cartStore.calculatePriceOrder(input);

  calculateLoading.value = false;
};

watch(
  () => [
    numberOfRemainFields.value,
    productsData.value,
    billingAddressData.value.prefectureId,
    shippingCarrierId.value,
    paymentData.value.paymentMethodCode,
    shippingAddressData.value.prefectureId,
    shippingAddressData.value.sameWithBillingAddress,
    shippingAddressData.value.id,
    couponCode.value,
  ],
  () => {
    if (numberOfRemainFields.value != 0) return;

    if (calculateLoading.value) return;

    calculateLoading.value = true;

    debounce(calculatePriceOrder, 200)();
  },
  { deep: true, flush: "post" },
);
</script>

<style scoped>
.header-title {
  background-color: #000a4c;
  color: #fff;
}

.custom-title {
  color: black;
}

.table {
  th,
  td {
    padding: 5px 4px !important;
  }
}
</style>
